/**
 * AnalysisSpec — the optional, versioned `analysis` sublayer of the M4 MapSpec
 * (M8). It rides INSIDE the MapSpec (persisted in `map.style` / the M6 snapshot),
 * so every analysis artifact serializes, saves, shares, and embeds through the
 * same one-spec contract — never a parallel representation. The renderer treats
 * analysis overlays as ADDITIONAL spec-driven GPU layers, so a MapSpec with no
 * `analysis` block renders byte-identically to before it existed.
 *
 * Versioning is on the block itself (`analysis.version`), independent of
 * MAPSPEC_VERSION: a v(N-1) analysis block migrates up under vN code via the
 * migration registry below, exactly like the top-level MapSpec migrations.
 *
 * M8-A populates `territories` (lasso / automated / boundary) and `radii`.
 * `driveTime` and `routes` are DEFINED here but stay unpopulated until M8-B
 * (drive-time isochrones + route optimization) — their shapes are frozen now so
 * the one-spec contract is stable across the split.
 */

import { z } from "zod";

export const ANALYSIS_VERSION = 1;

/** A single [lng, lat] vertex — MapLibre/GeoJSON coordinate order. */
export const LngLatSchema = z.tuple([z.number(), z.number()]);
export type LngLat = z.infer<typeof LngLatSchema>;

/** A polygon outer ring: an ordered list of [lng, lat] vertices (open or closed). */
export const RingSchema = z.array(LngLatSchema);
export type Ring = z.infer<typeof RingSchema>;

/* ----------------------------------------------------------- territories */

export const BoundaryKindSchema = z.enum(["zip", "county", "state"]);
export type BoundaryKind = z.infer<typeof BoundaryKindSchema>;

/** A reference to an administrative boundary a territory was assigned from. */
export const BoundaryRefSchema = z.object({
  kind: BoundaryKindSchema,
  /** Stable boundary id (ZIP code, county FIPS, state code). */
  id: z.string(),
});
export type BoundaryRef = z.infer<typeof BoundaryRefSchema>;

/**
 * Per-territory rollup M9 (Demographics) will extend. M8-A carries a member
 * count and, for automated/boundary balancing, the sum of the numeric column.
 */
export const TerritoryRollupSchema = z.object({
  count: z.number(),
  sum: z.number().optional(),
  /** The numeric column `sum` is taken over (mirrors `balanceBy`). */
  balanceBy: z.string().optional(),
});
export type TerritoryRollup = z.infer<typeof TerritoryRollupSchema>;

export const TerritorySchema = z.object({
  id: z.string(),
  name: z.string(),
  /** Persisted fill/outline color (a palette hex) — same value everywhere. */
  color: z.string(),
  /** Outer ring: the drawn lasso, the automated hull, or the boundary geometry. */
  polygon: RingSchema.optional(),
  /** Present only for boundary-mode territories. */
  boundaryRef: BoundaryRefSchema.optional(),
  /** The enclosed/assigned rows (M9 reads this to compute demographic rollups). */
  memberRowIds: z.array(z.string()),
  rollup: TerritoryRollupSchema.optional(),
});
export type Territory = z.infer<typeof TerritorySchema>;

export const TerritoryModeSchema = z.enum(["lasso", "boundary", "automated"]);
export type TerritoryMode = z.infer<typeof TerritoryModeSchema>;

export const TerritoriesSchema = z.object({
  mode: TerritoryModeSchema,
  items: z.array(TerritorySchema),
  /** Automated mode: the numeric column territories were balanced by (count when absent). */
  balanceBy: z.string().optional(),
});
export type Territories = z.infer<typeof TerritoriesSchema>;

/* ---------------------------------------------------------------- radii */

/** A radius circle center: an explicit [lng,lat] or a `rowId` to center on. */
export const RadiusCenterSchema = z.union([LngLatSchema, z.string()]);
export type RadiusCenter = z.infer<typeof RadiusCenterSchema>;

export const RadiusCircleSchema = z.object({
  id: z.string(),
  center: RadiusCenterSchema,
  radiusMeters: z.number().positive(),
  /** The rows computed to fall inside this circle. */
  insideRowIds: z.array(z.string()),
  /** Optional caption (e.g. "10 mi") — display only. */
  label: z.string().optional(),
});
export type RadiusCircle = z.infer<typeof RadiusCircleSchema>;

/* ----------------------------------------------- drive-time (M8-B seam) */

export const DriveTimeBandSchema = z.object({
  minutes: z.number().optional(),
  miles: z.number().optional(),
  /** The isochrone polygon returned by the geo-core routing provider. */
  polygon: RingSchema,
  style: z.object({ color: z.string(), opacity: z.number() }).optional(),
});
export type DriveTimeBand = z.infer<typeof DriveTimeBandSchema>;

export const DriveTimeSchema = z.object({
  originRefs: z.union([z.array(z.string()), z.literal("selected")]),
  bands: z.array(DriveTimeBandSchema),
});
export type DriveTime = z.infer<typeof DriveTimeSchema>;

/* ---------------------------------------------------- routes (M8-B seam) */

export const RouteTotalsSchema = z.object({ distance: z.number(), duration: z.number() });
export type RouteTotals = z.infer<typeof RouteTotalsSchema>;

export const RoutesSchema = z.object({
  /** Ordered after optimize; cap ~70 enforced by the tool, not the schema. */
  stopRowIds: z.array(z.string()),
  optimized: z.boolean(),
  totals: RouteTotalsSchema.optional(),
});
export type Routes = z.infer<typeof RoutesSchema>;

/* --------------------------------------------------------- the top block */

export const AnalysisSpecSchema = z.object({
  version: z.literal(ANALYSIS_VERSION),
  territories: TerritoriesSchema.optional(),
  radii: z.array(RadiusCircleSchema).optional(),
  driveTime: DriveTimeSchema.optional(),
  routes: RoutesSchema.optional(),
});
export type AnalysisSpec = z.infer<typeof AnalysisSpecSchema>;

/** A fresh, empty analysis block at the current version. */
export function emptyAnalysis(): AnalysisSpec {
  return { version: ANALYSIS_VERSION };
}

/** Validate an unknown value as a current-version AnalysisSpec. */
export function parseAnalysis(input: unknown): AnalysisSpec {
  return AnalysisSpecSchema.parse(input);
}

/**
 * Analysis migration registry, keyed by the version being migrated FROM. A
 * pre-versioned block (no `version` field) is treated as v0 and adopted under
 * v1; content-reshaping migrations register here as ANALYSIS_VERSION climbs
 * (e.g. `1: (a) => ({ ...a, version: 2, /* reshape *​/ })`).
 */
type AnalysisMigration = (a: Record<string, unknown>) => Record<string, unknown>;
const ANALYSIS_MIGRATIONS: Record<number, AnalysisMigration> = {
  0: (a) => ({ ...a, version: ANALYSIS_VERSION }),
};

/**
 * Migrate an analysis block of any supported prior version up to the current
 * version, then validate. A current-version block passes straight through.
 * Exported so `migrateMapSpec` can pre-migrate the nested block before the
 * strict MapSpec validator (which pins `analysis.version` to the literal) runs.
 */
export function migrateAnalysis(input: unknown): AnalysisSpec {
  if (typeof input !== "object" || input === null) {
    throw new Error("analysis must be an object");
  }
  let a = input as Record<string, unknown>;
  let version = typeof a.version === "number" ? a.version : 0;
  if (version > ANALYSIS_VERSION) {
    throw new Error(`analysis version ${version} is newer than supported (${ANALYSIS_VERSION})`);
  }
  while (version < ANALYSIS_VERSION) {
    const migrate = ANALYSIS_MIGRATIONS[version];
    if (!migrate) throw new Error(`No migration from analysis version ${version}`);
    a = migrate(a);
    version = a.version as number;
  }
  return AnalysisSpecSchema.parse(a);
}
